
import { Component, Prop, Watch } from 'vue-property-decorator';
import { mixins } from 'vue-class-component';
import { ValidationObserver } from 'vee-validate';
import Notification from '@/mixins/Notification';
import moment from 'moment';

interface OpeningHour {
  date: string | null;
  openedAt: string | null;
  closedAt: string | null;
  dateAtMenu: false;
  openedAtMenu: false;
  closedAtMenu: false;
  isClosed: boolean;
}

@Component
export default class VSpecialOpeningHour extends mixins(Notification) {
  @Prop({ type: String, default: '' }) public title!: string;
  @Prop({ type: String, default: '' }) public infoText!: string;
  @Prop({ type: String, default: 'notification.needOpeningHours' }) public noHourError!: string;
  @Prop({ type: Boolean, default: false }) public preventSpecial!: boolean;
  @Prop({ type: Array }) public availableWeekdays!: any[];

  public $refs!: {
    observer: InstanceType<typeof ValidationObserver>;
  };
  public hours: OpeningHour[] = [];

  get weekdays() {
    return [
      { value: 0, text: this.$t('weekdays.monday') },
      { value: 1, text: this.$t('weekdays.tuesday') },
      { value: 2, text: this.$t('weekdays.wednesday') },
      { value: 3, text: this.$t('weekdays.thursday') },
      { value: 4, text: this.$t('weekdays.friday') },
      { value: 5, text: this.$t('weekdays.saturday') },
      { value: 6, text: this.$t('weekdays.sunday') },
    ];
  }

  get availableWeekdaysMapped() {
    return this.availableWeekdays.map((w: number) => w + 1);
  }

  get allowedDates() {
    return (date: string) => {
      const formatted = moment(date).format('YYYY-MM-DD');
      const weekday = moment(date).isoWeekday();

      if (!this.availableWeekdaysMapped.includes(weekday)) {
        return false;
      }

      const exists = this.hours.some((h) => h.date === formatted);

      return !exists;
    };
  }

  public addOpeningHour() {
    if (this.preventSpecial) {
      return this.notifyError(this.noHourError);
    }

    this.hours.push({
      date: null,
      openedAt: null,
      openedAtMenu: false,
      dateAtMenu: false,
      closedAt: null,
      closedAtMenu: false,
      isClosed: false,
    });
  }

  public getHours() {
    return this.hours.map((h: OpeningHour) => {
      return {
        ...h,
        openedAt: this.getUtcTime(h.openedAt!),
        closedAt: this.getUtcTime(h.closedAt!),
      };
    });
  }

  public setHours(hours: OpeningHour[], overwrite: boolean = false): void {
    if (overwrite) {
      this.hours = [];
    }
    for (const hour of hours) {
      this.hours.push({
        date: moment(hour.date).isValid() ? moment(hour.date).format('YYYY-MM-DD') : '',
        openedAtMenu: false,
        closedAtMenu: false,
        dateAtMenu: false,
        isClosed: !!hour.isClosed,
        // @ts-ignore
        openedAt: this.toLocaleTime(hour.openedAt),
        // @ts-ignore
        closedAt: this.toLocaleTime(hour.closedAt),
      });
    }
  }

  public getUtcTime(time: string): string | null {
    if (!moment(time, 'HH:mm').isValid()) {
      return null;
    }

    return moment(time, 'HH:mm').subtract(120, 'minutes').format('HH:mm');
  }

  public toLocaleTime(time: string): string | null {
    if (!moment(time, 'HH:mm').isValid()) {
      return null;
    }

    return moment(time, 'HH:mm').add(120, 'minutes').format('HH:mm');
  }

  public removeHour(item: OpeningHour) {
    this.hours.splice(this.hours.indexOf(item), 1);
  }

  public async validate(): Promise<boolean> {
    const nativeValidation = await this.$refs.observer.validate();

    const failed = this.hours.find(
      (h: OpeningHour) =>
        (!h.isClosed && (!h.openedAt || !h.closedAt)) ||
        (h.isClosed && ((h.openedAt && !h.closedAt) || (!h.openedAt && h.closedAt))),
    );

    if (!nativeValidation || (nativeValidation && !failed)) {
      return nativeValidation;
    }

    const index = this.hours.findIndex(
      (h: OpeningHour) =>
        h.isClosed === failed?.isClosed && h.openedAt === failed?.openedAt && h.closedAt === failed?.closedAt,
    );

    this.$refs.observer.setErrors(
      !failed?.openedAt
        ? { [`${this.$t('common.from')} ${index + 1}`]: 'required' }
        : !failed?.closedAt
        ? { [`${this.$t('common.to')} ${index + 1}`]: 'required' }
        : {},
    );

    return Promise.resolve(false);
  }

  @Watch('availableWeekdaysMapped')
  public onAvailableWeekdaysChange() {
    this.hours = this.hours.filter((h: OpeningHour) => {
      const weekday = moment(h.date).isoWeekday();

      return this.availableWeekdaysMapped.includes(weekday);
    });
  }
}
